/**
 * โมเดลสมดุล (PLAN ขั้น 4.5) — ประกอบ "ผู้เล่นอ้างอิง" แล้วจำลองการสู้แบบค่าคาดหมาย (ไม่สุ่ม)
 *
 * **ท่อเดียว:** `assemblePlayer` คือท่อที่ server ใช้จริงใน `refreshDerived`
 * (equipTotals → cardTotals(+ออปชัน+เซ็ต) → passiveTotals → mergeModifiers → jobStatBonus → deriveStats)
 * ตัวจำลองจึงเห็นค่าเดียวกับในเกมเสมอ ไม่มีสูตรซ้ำ
 *
 * ค่าที่ปรับได้อยู่ใน `data/balance.json` ทั้งหมด · ตัวแก้สมการมอน `tools/balance.ts`
 * pure function ทั้งไฟล์
 */
import balanceJson from "./data/balance.json" with { type: "json" };
import { CLASSES, CLASS_LIST, ITEM_LIST, ITEMS, getAllSkillsForClass } from "./data.js";
import { damage, deriveStats, hitChance, jobStatBonus, jobLevelCap, statCost, statPointsForLevel, STAT_CAP, } from "./formulas.js";
import { equipDenial, equipTotals, heldWeapons, isTwoHanded, mergeModifiers, withEquipStats } from "./equip.js";
import { cardTotals } from "./cards.js";
import { passiveTotals } from "./passives.js";
import { skillAtLevel, weaponNeedMet } from "./skills.js";
import { sizeMul, sizeOf } from "./sizes.js";
import { OPTION_REROLL_GOLD } from "./options.js";
import { ENDOW_GOLD_PER_LV } from "./endow.js";
import { EQUIP_SLOTS, STAT_KEYS } from "./types.js";
/**
 * รวมทุกแหล่งพลังเป็นค่ารองชุดเดียว — **ที่เดียวในเกม** (server `refreshDerived` เรียกตัวนี้)
 * @param extra ตัวปรับจากบัฟ/ดีบัฟ (server) หรืองบจำลอง (ตัวจำลอง) ต่อท้ายสุด
 */
export function assemblePlayer(cls, baseStats, baseLevel, jobLevel, loadout, extra = {}) {
    const gear = equipTotals(loadout.equipped, ITEMS, loadout.refines);
    const cards = cardTotals(loadout.equipped, loadout.cards ?? {}, ITEMS, loadout.options, loadout.refines);
    const held = heldWeapons(cls.id, loadout.equipped, ITEMS);
    const passive = passiveTotals(getAllSkillsForClass(cls.id), loadout.skills, held);
    const mods = mergeModifiers(mergeModifiers(mergeModifiers(gear.mods, cards.mods), passive.mods), extra);
    const stats = withEquipStats(withEquipStats(withEquipStats(withEquipStats(baseStats, jobStatBonus(cls, jobLevel)), gear.stats), cards.stats), passive.stats);
    return { derived: deriveStats(cls, stats, baseLevel, mods), stats, cardFx: cards, elementDmg: passive.elementDmg, held };
}
export const BUILD_MODES = ["min", "normal", "full"];
export const BALANCE = balanceJson;
/** อาชีพขั้นต้นทั้งหกที่ใช้เป็นบิลด์อ้างอิง (เลเวลถึง `advanceAt` เปลี่ยนเป็นขั้นสูง) */
export const BASE_CLASSES = CLASS_LIST.filter((c) => !c.tier || c.tier === 1).map((c) => c.id);
/** อาชีพที่ใช้ที่เลเวลนี้ (ขั้นสูงตัวแรกที่ต่อจากอาชีพเริ่มต้น) */
export function classAtLevel(baseClass, level) {
    const base = CLASSES[baseClass];
    if (level < BALANCE.advanceAt)
        return base;
    return CLASS_LIST.find((c) => c.baseClass === baseClass) ?? base;
}
/** Job Level ที่คาดที่ Base Level นี้ (ขั้นต้นตันก่อนเลื่อนขั้น ขั้นสูงโตช้ากว่า) */
export function jobAtLevel(cls, level) {
    const cap = jobLevelCap(cls);
    if (cls.tier === 2)
        return Math.max(1, Math.min(cap, Math.round((level - BALANCE.advanceAt) * 0.6) + 1));
    return Math.max(1, Math.min(cap, level));
}
/** สเตตัสฐานของบิลด์อ้างอิง: ลงแต้มทั้งหมดที่ได้ถึงเลเวลนี้ตามสัดส่วนใน balance.json */
export function referenceStats(baseClass, level) {
    const cls = CLASSES[baseClass];
    const st = { ...cls.stats };
    const w = BALANCE.builds[baseClass] ?? { [cls.mainStat]: 1 };
    let points = 0;
    for (let lv = 1; lv <= level; lv++)
        points += statPointsForLevel(lv);
    const keys = STAT_KEYS.filter((k) => (w[k] ?? 0) > 0);
    // ลงทีละแต้ม: เลือกสเตตัสที่ "ต่ำกว่าสัดส่วนเป้า" มากสุด
    for (;;) {
        const total = keys.reduce((s, k) => s + st[k], 0);
        const pick = keys
            .filter((k) => st[k] < STAT_CAP && statCost(st[k]) <= points)
            .sort((a, b) => st[a] / (w[a] * total) - st[b] / (w[b] * total))[0];
        if (!pick)
            break;
        points -= statCost(st[pick]);
        st[pick]++;
    }
    return st;
}
/** คะแนนคร่าว ๆ ของของชิ้นหนึ่ง ใช้เลือกของที่ดีสุดต่อช่อง (บิลด์อ้างอิงเท่านั้น) */
function gearScore(def, cls) {
    const b = def.bonus ?? {};
    const magic = cls.autoKind === "magic";
    let s = (magic ? b.weaponMatk ?? 0 : b.weaponAtk ?? 0) * 2 + (b.flatDef ?? 0) + (b.flatCrit ?? 0) + (b.flatBlock ?? 0) * 100;
    for (const k of STAT_KEYS)
        s += (b.stats?.[k] ?? 0) * (k === cls.mainStat ? 3 : 1);
    s += ((b.hpMul ?? 1) - 1) * 300 + ((b.aspdMul ?? 1) - 1) * 300;
    return s + (def.reqLevel ?? 1) * 0.01;
}
/** ของที่ดีสุดต่อช่องที่อาชีพนี้ใส่ได้ ณ เลเวลนี้ (เกราะ/เครื่องประดับ/อาวุธ จาก items.json จริง) */
export function referenceGear(cls, level) {
    const out = {};
    const best = (slot, ok = () => true) => ITEM_LIST.filter((d) => d.kind === "equip" && ok(d) && !equipDenial(d, slot, cls.id, level))
        .sort((a, b) => gearScore(b, cls) - gearScore(a, cls))[0];
    for (const s of EQUIP_SLOTS) {
        if (s.id === "weapon" || s.id === "offhand" || s.id === "pet")
            continue;
        const d = best(s.id);
        if (d)
            out[s.id] = d.id;
    }
    // อาวุธ: สองมือ vs หนึ่งมือ + มือรอง เลือกคะแนนรวมสูงกว่า
    const two = best("weapon", (d) => isTwoHanded(d));
    const one = best("weapon", (d) => !isTwoHanded(d));
    const off = best("offhand");
    const score = (d) => (d ? gearScore(d, cls) : 0);
    if (two && score(two) >= score(one) + score(off))
        out["weapon"] = two.id;
    else {
        if (one)
            out["weapon"] = one.id;
        if (off)
            out["offhand"] = off.id;
    }
    return out;
}
/** สกิลโจมตีที่ใช้ได้ (ลงแต้มได้ที่ Job นี้ ถือตรง) */
function attackSkills(cls, job, held) {
    return getAllSkillsForClass(cls.id).filter((s) => (s.kind === "physical" || s.kind === "magic") && s.power > 0 && s.targeting === "enemy"
        && (s.job ?? 1) <= (cls.tier === 2 && CLASSES[cls.baseClass ?? ""]?.skills.some((x) => x.id === s.id) ? 99 : job)
        && weaponNeedMet(s, held));
}
/** ประกอบผู้เล่นอ้างอิงของอาชีพ × เลเวล × ระดับบิลด์ ผ่าน `assemblePlayer` (ท่อจริง) */
export function referencePlayer(baseClass, level, mode) {
    const m = BALANCE.modes[mode];
    const cls = classAtLevel(baseClass, level);
    const job = jobAtLevel(cls, level);
    // เลเวลสกิลจำกัดด้วยแต้มที่มีจริง (ราว 1 เลเวลสกิลต่อ 3 Job ของต้นเกม) ไม่ให้ Lv 1 ใช้สกิลระดับ 8
    const skillLv = Math.max(1, Math.min(m.skillLv, cls.tier === 2 ? m.skillLv : Math.floor(job / 3)));
    const gear = referenceGear(cls, Math.max(1, level - m.gearLag));
    const refines = {};
    for (const slot of Object.keys(gear))
        if (slot !== "pet")
            refines[slot] = m.refine;
    // สกิลติดตัวทุกตัวที่ลงได้ที่เลเวลสกิลของบิลด์นี้ (ประมาณ — จริงแต้มจำกัดตามสาย)
    const skills = {};
    for (const s of getAllSkillsForClass(cls.id))
        if (s.kind === "passive")
            skills[s.id] = Math.min(skillLv, s.maxLv ?? 10);
    const extra = {};
    if (m.atkPct)
        extra.atkMul = 1 + m.atkPct, extra.matkMul = 1 + m.atkPct;
    if (m.hpPct)
        extra.hpMul = 1 + m.hpPct;
    if (m.def)
        extra.flatDef = m.def;
    const a = assemblePlayer(cls, referenceStats(baseClass, level), level, job, { equipped: gear, refines, skills }, extra);
    const magic = cls.autoKind === "magic";
    // สกิลโจมตี: เลือกสามตัวที่ให้พลังต่อวินาทีสูงสุด (ร่ายทุกครั้งที่คูลดาวน์หมด)
    const rates = attackSkills(cls, job, a.held)
        .map((s) => skillAtLevel(s, skillLv))
        .map((s) => (s.power * Math.max(1, s.hits)) / Math.max(1.5, s.cd))
        .sort((x, y) => y - x)
        .slice(0, 3);
    return {
        baseClass, cls, level, mode, derived: a.derived, vit: a.stats.VIT, magic, held: a.held,
        skillRate: rates.reduce((s, r) => s + r, 0), elementMul: m.element, gear,
    };
}
/** ดาเมจเฉลี่ยต่อครั้งที่ตัวคูณสกิล 1 (รวมโอกาสโดน คริ ขนาด ธาตุ) */
function expectedUnit(p, mon) {
    const hc = hitChance(p.derived.hit, mon.flee) / 100;
    const c = Math.min(1, p.derived.crit / 100);
    const power = p.magic ? p.derived.matk : p.derived.atk;
    const per = (1 - c) * damage(power, 1, mon.def, 0, false) + c * damage(power, 1, mon.def, 0, true);
    return hc * per * sizeMul(p.held.weapon, sizeOf(mon), p.magic) * p.elementMul;
}
/** จำลองผู้เล่น 1 ต่อ 1 กับมอน (ค่าคาดหมาย ไม่สุ่ม) */
export function simulateFight(p, mon) {
    const unit = expectedUnit(p, mon);
    const dps = unit * (p.derived.aspd + p.skillRate);
    const mhc = hitChance(mon.hit, p.derived.flee) / 100;
    const taken = mhc * (1 - Math.min(0.9, p.derived.block)) * damage(mon.atk, 1, p.derived.def, p.vit, false) * (mon.aspd || 1);
    return {
        dps,
        taken,
        ttk: mon.hp / Math.max(0.001, dps),
        survive: p.derived.maxHp / Math.max(0.001, taken),
        hit: hitChance(p.derived.hit, mon.flee),
    };
}
/** TTK เป้าของมอนปกติที่เลเวลนี้ (ต้นเกมเร็วกว่า ไต่ถึง normalTtk ที่ earlyUntil) */
export function targetTtk(level) {
    const t = BALANCE.targets;
    const k = Math.min(1, Math.max(0, (level - 1) / Math.max(1, t.earlyUntil - 1)));
    return t.earlyTtk + (t.normalTtk - t.earlyTtk) * k;
}
/** ราคาตะกร้าเผาเงินต่อชั่วโมงที่เลเวลนี้ (ราคาจริงจาก items.json) */
export function burnPerHour(lv) {
    const g = BALANCE.gold;
    const potion = lv < 20 ? "potion_small" : lv < 50 ? "potion_medium" : "potion_large";
    let sum = (ITEMS[potion]?.price ?? 0) * g.potionPerHour;
    for (const b of g.basket)
        if (lv >= b.from)
            sum += (ITEMS[b.item]?.price ?? 0) * b.perHour;
    // ทอยออปชันซ้ำ (OPTION_REROLL_GOLD ตามชั้นของ) และร่ายธาตุอาวุธ (300 × เลเวลอาวุธ)
    const reroll = OPTION_REROLL_GOLD[Math.min(OPTION_REROLL_GOLD.length - 1, lv < 20 ? 0 : lv < 40 ? 1 : lv < 60 ? 2 : 3)] ?? 0;
    sum += reroll * g.rerollPerHour + ENDOW_GOLD_PER_LV * lv * g.endowPerHour;
    return sum;
}
/** มัธยฐาน (ใช้รวมผลทุกอาชีพ ไม่ให้อาชีพเดียวลากค่า) */
export function median(xs) {
    const s = [...xs].sort((a, b) => a - b);
    const n = s.length;
    return n ? (n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2) : 0;
}
//# sourceMappingURL=balance.js.map